/**
 * Copyright (c) 2025 Editoor Contributors
 *
 * Licensed under MIT License
 */

/**
 * Reports a document page's sync state to the shell, which shows it in the
 * connection indicator in the header (orange while edits have waited a while
 * or wait offline, red for an error or a locked session; details in its
 * tooltip). Pages need no indicator of their own. The page side is
 * reportSyncStatusToShell, the shell side SyncOverlayState.
 */

import type { YjsSync, SyncStatusInfo } from './yjsSync';
import { setShellStatus, type ShellSyncState } from './shellMenu';

/** Bursts of changes (typing, a send) are posted at most this often. */
export const SYNC_STATUS_REPORT_INTERVAL_MS = 250;

function toShellState(info: SyncStatusInfo): ShellSyncState {
    const state: ShellSyncState = { status: info.status, pendingCount: info.pendingCount };
    if (info.error) state.error = info.error;
    return state;
}

function sameState(a: ShellSyncState | null, b: ShellSyncState): boolean {
    return a !== null && a.status === b.status && a.pendingCount === b.pendingCount && a.error === b.error;
}

/**
 * Posts the current sync state at once, then each change, coalesced: a burst
 * is posted as its final state at the end of the interval, and a state the
 * shell already has is not posted again. Returns a function that stops the
 * reporting (call it where the page destroys the YjsSync).
 */
export function reportSyncStatusToShell(yjsSync: YjsSync): () => void {
    let lastPosted: ShellSyncState | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const post = () => {
        const state = toShellState(yjsSync.getSyncStatus());
        if (sameState(lastPosted, state)) return;
        lastPosted = state;
        setShellStatus({ sync: state });
    };

    post();
    const unsubscribe = yjsSync.onSyncStatusChange(() => {
        if (timer !== null) return;
        timer = setTimeout(() => {
            timer = null;
            post();
        }, SYNC_STATUS_REPORT_INTERVAL_MS);
    });

    return () => {
        unsubscribe();
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
    };
}

/** Edits queued online show only after waiting this long (an ordinary send never shows). */
export const SYNC_PENDING_DELAY_MS = 3000;

/** What the connection indicator shows for the sync state, and its tooltip line. */
export interface SyncOverlayView {
    level: 'none' | 'pending' | 'error';
    line: string | null;
}

/**
 * The shell side: turns the reported sync state into what the indicator
 * shows. Red for an error or a locked session (an `error` text is only
 * reported then). A queue shows orange at once when offline, else only once
 * it has stood for SYNC_PENDING_DELAY_MS; it clears as soon as it empties.
 * `onChange` is called when the delay makes the queue show.
 */
export class SyncOverlayState {
    private sync: ShellSyncState | null = null;
    private pendingShown = false;
    private timer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private readonly onChange: () => void,
        private readonly isOffline: () => boolean,
        private readonly delayMs: number = SYNC_PENDING_DELAY_MS,
    ) {}

    /** The loaded app's latest state (null: none, e.g. another app loaded). */
    set(sync: ShellSyncState | null): void {
        this.sync = sync;
        this.evaluate();
    }

    /** The device or the server went offline or came back. */
    offlineChanged(): void {
        this.evaluate();
    }

    view(): SyncOverlayView {
        const sync = this.sync;
        if (sync && (sync.status === 'error' || sync.error)) {
            return { level: 'error', line: sync.error || 'Sync error' };
        }
        if (sync && this.pendingShown) {
            const count = sync.pendingCount;
            return { level: 'pending', line: `${count} ${count === 1 ? 'change' : 'changes'} waiting — sent when the server is reachable` };
        }
        return { level: 'none', line: null };
    }

    private evaluate(): void {
        const queued = (this.sync?.pendingCount ?? 0) > 0;
        if (!queued) {
            this.cancelTimer();
            this.pendingShown = false;
        } else if (this.isOffline()) {
            this.cancelTimer();
            this.pendingShown = true;
        } else if (!this.pendingShown && this.timer === null) {
            this.timer = setTimeout(() => {
                this.timer = null;
                if ((this.sync?.pendingCount ?? 0) === 0) return;
                this.pendingShown = true;
                this.onChange();
            }, this.delayMs);
        }
    }

    private cancelTimer(): void {
        if (this.timer !== null) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }
}
